import React, { useEffect, useState, useRef } from 'react'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import simulate from '../lib/simulate.js'
import ipsc from '../lib/ipsc.js'
import toFixed from '../lib/toFixed.js'
import calcBarrelWeight from '../lib/barrelWeight.js'
import energy from '../lib/energy.js'
import { estimateVelocityFromCartridge } from '../lib/estimateVelocity.js'
import { estimateRiflePrecision } from '../lib/rifle-precision.js'
import recoil from '../lib/recoil'

import allScopes from '../lib/data/scopes.json'
import stocks from '../lib/data/stocks.json'
import actions from '../lib/data/actions.json'
import contours from '../lib/data/contours.json'
import cartridges from '../lib/data/cartridges.json'
import bullets from '../lib/data/bullets.json'

const scopes = allScopes.filter(v => v.name.includes('Nightforce'))

const bulletMap = {}
bullets.forEach(bullet => {
  const cal = bullet.caliber.toFixed(3)
  if (!bulletMap[cal]) bulletMap[cal] = []
  bulletMap[cal].push(bullet)
})

const calibers = [
  ".172",
  ".204",
  ".224",
  ".243",
  ".257",
  ".264",
  ".277",
  ".284",
  ".308",
  ".338",
  ".375",
  ".408",
  ".416",
  ".458",
  ".510"
]

const defaultProfile = {
  range: "1000",
  v0: "2800",
  bc: ".3",
  wind: [ { speed: "10", direction: "90" } ],
  useAerodynamicJump: true,
  useSpinDrift: true,
  latitude: "45",
  azimuth: "0",
  lookAngle: "0",
  pressure: "29.92",
  temperature: "59",
  humidity: "50",
  scopeHeight: "2",
}

const within = (v, { min, max }) => {
  return v >= parseFloat(min) && v <= parseFloat(max)
}

function GunDesigner() {
  const [ showAdvanced, setShowAdvanced ] = useAutoSaveState('showAdvanced', false)
  const [ variables, setVariables ] = useAutoSaveState('variables', null)
  const [ results, setResults ] = useState(null)
  const [testCount, setTestCount] = useState(0)
  const [comboCount, setComboCount] = useAutoSaveState('comboCount', "100")
  const [optimize, setOptimize] = useAutoSaveState('optimize', "1")
  const [ gun, setGun ] = useAutoSaveState('gun', {
    barrelLength: "26",
    contour: null,
    cartridge: null,
    bullet: null,
    scope: null,
    action: null,
    stock: null
  })
  const [ locks, setLocks ] = useAutoSaveState('locks', {})
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [ constraints, setConstraints ] = useAutoSaveState('constraints', {
    weight: { min: "0", max: "100", units: "lbs" },
    muzzleEnergy: { min: "0", max: "50000", units: "ft/lbs" },
    targetEnergy: { min: "0", max: "50000", units: "ft/lbs" },
    muzzleVelocity: { min: "0", max: "5000", units: "fps" },
    targetVelocity: { min: "0", max: "5000", units: "fps" },
    barrelLength: { min: "16", max: "32", units: "in" },
    caliber: { min: ".172", max: ".510", units: "in" },
    recoil: { min: "0", max: "1000", units: "ft⋅lb" },
  })

  const canvasRef = useRef(null)

  useEffect(() => {
    setInitialVariables(defaultProfile)
  }, [])

  const randEl = arr => arr[Math.floor(arr.length * Math.random())]

  useEffect(() => {
    if (!gun.contour || !gun.bullet || !gun.cartridge || !gun.scope || !gun.action || !gun.stock || !gun.barrelLength) return
    if (results && results.gun === gun) return
    const combo = { ...gun }
    populateCombo(combo)
    console.log(combo)
    const simResult = runSimulation(combo)
    if (simResult) {
      simResult.gun = combo
      setResults(simResult)
      console.log('set gun', combo)
      setGun(combo)
      setPreview(`1st Round: ${toFixed(simResult.prob1, 2)}%, 2nd Round: ${toFixed(simResult.prob2, 2)}%, Weight: ${toFixed(combo.weight, 1)} lbs`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ gun ])

  const getCombos = async () => {
    window.gtag('event', 'calculate-gun-designer')
    console.log({ comboCount })
    let best = 0
    for (let i = 0; i < parseInt(comboCount); ++i) {
      let valid = false
      let combo
      const maxAttempts = 1000
      let attempts = 0
      while (!valid && attempts < maxAttempts) {
        ++attempts
        combo = {
          scope: (locks.scope && gun.scope) ? gun.scope : randEl(scopes),
          stock: (locks.stock && gun.stock) ? gun.stock : randEl(stocks),
          action: (locks.action && gun.action) ? gun.action : randEl(actions),
          contour: (locks.contour && gun.contour) ? gun.contour : randEl(contours),
          cartridge: (locks.cartridge && gun.cartridge) ? gun.cartridge : randEl(cartridges),
          barrelLength: (locks.barrelLength && gun.barrelLength) ? gun.barrelLength : Math.round(Math.random() * (parseInt(constraints.barrelLength.max) - parseInt(constraints.barrelLength.min))) + parseInt(constraints.barrelLength.min)
        }
        combo.bullet = randEl(bulletMap[combo.cartridge.caliber.toFixed(3)])
        combo.barrelLength = Math.min(combo.barrelLength, combo.contour.length)
        populateCombo(combo)
        valid =
          within(combo.bullet.caliber, constraints.caliber) &&
          within(combo.weight, constraints.weight) &&
          within(combo.v0, constraints.muzzleVelocity) &&
          within(combo.muzzleEnergy, constraints.muzzleEnergy) &&
          within(combo.recoil, constraints.recoil)
      }
      if (!valid) return
      setTestCount(i + 1)
      const simResult = runSimulation(combo)
      if (simResult) {
        const { points1, points2, hits1, hits2, prob1, prob2 } = simResult
        const bestProb = optimize === "1" ? prob1 : prob2
        console.log({ bestProb, best })
        if (bestProb > best) {
          best = bestProb
          //console.log(combo, { points1, points2, hits1, hits2, prob1, prob2 })
          setGun(combo)
          drawPoints(points1, points2)
          setResults({ prob1, prob2 })
          await new Promise(resolve => setTimeout(resolve, 15))
        }
      }
      await new Promise(resolve => setTimeout(resolve, 1))
    }
  }

  const populateCombo = combo => {
    const weights = {
      barrel: calcBarrelWeight(combo.contour, combo.bullet.caliber, combo.barrelLength),
      action: (combo.cartridge.short ? combo.action.short : combo.action.long) / 16,
      stock: combo.stock.weight,
      scope: combo.scope.weight / 16
    }
    combo.powderWeight = combo.bullet.weight / 3
    combo.weight = Math.round((weights.barrel + weights.action + weights.stock + weights.scope) * 10) / 10
    combo.v0 = estimateVelocityFromCartridge(combo.bullet, combo.cartridge, combo.barrelLength)
    combo.muzzleEnergy = energy(combo.bullet.weight, combo.v0)
    combo.recoil = recoil(combo.weight, combo.bullet.weight, combo.powderWeight, combo.v0).freeRecoilEnergy
    combo.groupSize = estimateRiflePrecision(combo.v0, combo.bullet.bc, combo.bullet.twist, .0001, combo.bullet.weight, combo.powderWeight, combo.weight, combo.scope.maxZoom)
  }

  const runSimulation = combo => {
    const profile = {
      wind: [ { speed: "10", direction: "90" } ],
      useAerodynamicJump: true,
      useSpinDrift: true,
      scopeHeight: "2",
      bulletWeight: combo.bullet.weight,
    }
    const comboVariables = variables.map(variable => {
      if (variable.name === 'bc') return getDefaultVariableSetting('bc', { bc: combo.bullet.G7 })
      if (variable.name === 'v0') return { ...getDefaultVariableSetting('v0', { v0: combo.v0 }), measured: combo.v0, actual: combo.v0 }
      if (variable.name === 'groupSize') return {
        ...getDefaultVariableSetting('groupSize', { bc: combo.bullet.bc }),
        actual: combo.groupSize,
        sd: combo.groupSize / 2
      }
      return variable
    })
    const maxSims = 100
    const results = simulate(profile, comboVariables, constraints, maxSims)
    if (results) {
      results.gun = combo
    }
    return results
  }

  const drawPoints = (points1, points2) => {
    const canvas = canvasRef.current
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    console.log(canvas)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    ctx.fillStyle = '#999'
    const targetPx = 100
    const target = getTarget()
    const pxScale = targetPx / (target.shape === 'ipsc' ? 18 : target.size)
    if (target.shape === 'circle') {
      ctx.beginPath()
      ctx.arc(ctx.canvas.width / 2, ctx.canvas.height / 2, targetPx / 2, 0, 2 * Math.PI)
      ctx.fill()
    } else if (target.shape === 'square') {
      ctx.fillRect(
        ctx.canvas.width / 2 - targetPx / 2,
        ctx.canvas.height / 2 - targetPx / 2,
        targetPx,
        targetPx
      )
    } else if (target.shape === 'ipsc') {
      ctx.beginPath()
      ctx.moveTo(
        -ipsc[0][0] * pxScale + ctx.canvas.height / 2,
        -ipsc[0][1] * pxScale + ctx.canvas.width / 2
      )
      ipsc.forEach(p => ctx.lineTo(
        -p[0] * pxScale + ctx.canvas.height / 2,
        -p[1] * pxScale + ctx.canvas.width / 2
      ))
      ctx.fill()
    }
    const drawImpact = ([drift, drop]) => {
      const gx = -drift * pxScale + ctx.canvas.width / 2
      const gy = -drop * pxScale + ctx.canvas.height / 2
      ctx.beginPath()
      ctx.arc(gx, gy, 1.5, 0, 2 * Math.PI)
      ctx.fill()
    }
    ctx.fillStyle = '#f00'
    points1.forEach(drawImpact)
    ctx.fillStyle = '#00f'
    points2.forEach(drawImpact)
  }

  const getTarget = () => {
    const targetVariable = variables.find(variable => variable.name === 'target')
    return targetVariable
  }

  const setInitialVariables = profile => {
    setVariables([
      getDefaultVariableSetting('target', profile),
      getDefaultVariableSetting('iterations', profile),
      getDefaultVariableSetting('groupSize', profile, { hidden: true }),
      getDefaultVariableSetting('v0', profile),
      getDefaultVariableSetting('range', profile),
      getDefaultVariableSetting('windSpeed', profile),
      getDefaultVariableSetting('windDirection', profile),
      getDefaultVariableSetting('verticalZero', profile, { hidden: true }),
      getDefaultVariableSetting('horizontalZero', profile, { hidden: true }),
      getDefaultVariableSetting('bc', profile, { hidden: true }),
      getDefaultVariableSetting('useAerodynamicJump', profile),
      getDefaultVariableSetting('useSpinDrift', profile),
      getDefaultVariableSetting('latitude', profile),
      getDefaultVariableSetting('azimuth', profile),
      getDefaultVariableSetting('humidity', profile, { hidden: true }),
      getDefaultVariableSetting('lookAngle', profile),
      getDefaultVariableSetting('cant', profile),
      getDefaultVariableSetting('pressure', profile),
      getDefaultVariableSetting('temperature', profile),
      getDefaultVariableSetting('scopeHeight', profile, { hidden: true }),
    ])
  }

  const [tab, setTab] = useState(0)

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={1} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" style={{ textAlign: 'center' }}>
            Gun Designer
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)}>
            <Tab label="Constraints" />
            <Tab label="Variables" />
            <Tab label="Simulation" />
          </Tabs>
        </Grid>
        {tab === 0 && <Grid item xs={12} mt={2}>
          <Paper>
            <Grid container spacing={2} p={2}>
              {Object.keys(constraints).map(constraint => <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={`Min ${toChipLabel(constraint)}`}
                    value={constraints[constraint].min}
                    onChange={e => {
                      setConstraints({
                        ...constraints,
                        [constraint]: {
                          ...constraints[constraint],
                          min: e.target.value
                        }
                      })
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">{constraints[constraint].units}</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={`Max ${toChipLabel(constraint)}`}
                    value={constraints[constraint].max}
                    onChange={e => {
                      setConstraints({
                        ...constraints,
                        [constraint]: {
                          ...constraints[constraint],
                          max: e.target.value
                        }
                      })
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">{constraints[constraint].units}</InputAdornment> }}
                  />
                </Grid>
              </>)}
            </Grid>
          </Paper>
        </Grid>}
        {tab === 1 && <Grid item xs={12}>
          <Grid container spacing={1} justifyContent="center" >
            {variables && variables.map((variable, i) => ((!variable.advanced || showAdvanced) && !variable.hidden && <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Variable
                  {...variable}
                  set={(key, value) => {
                    const newVariables = [ ...variables ]
                    newVariables[i] = { ...variable, [key]: value }
                    console.log({ variables, newVariables, key, value })
                    if (variable.name === 'groupSize' && key === 'actual') {
                      const sd = (value / 2).toFixed(2)
                      if (!isNaN(sd)) {
                        newVariables[i].sd = sd
                      }
                    }
                    if (variable.name === 'groupSize' && key === 'sd') {
                      const actual = (value * 2).toFixed(2)
                      if (!isNaN(actual)) {
                        newVariables[i].actual = actual
                      }
                    }
                    setVariables(newVariables)
                  }}
                />
              </Paper>
            </Grid>))}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdvanced}
                    onChange={e => setShowAdvanced(e.target.checked)}
                  />}
                label="Show Advanced Options"
              />
            </Grid>
          </Grid>
        </Grid>}
        <Grid item xs={12} hidden={tab !== 2} mt={2}>
          <Paper>
            <Grid container spacing={2} p={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Simulation Count"
                  value={comboCount}
                  onChange={e => setComboCount(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel>Optimize For</InputLabel>
                <Select value={optimize} onChange={e => setOptimize(e.target.value)} fullWidth>
                  <MenuItem value="1">1st Round Hit Probability</MenuItem>
                  <MenuItem value="2">2nd Round Hit Probability</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => getCombos()}
                >
                  Run Simulation
                </Button>
              </Grid>
            </Grid>
            {testCount > 0 && <Grid container p={2} justifyContent="center">
              <Typography variant="body1">{testCount} cases tested</Typography>
            </Grid>}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} hidden={tab !== 2}>
          <Paper>
            <Grid container spacing={1} style={{ marginTop: 0 }} p={2}>
              <Grid item xs={12}>
                <Typography variant="h4" style={{ textAlign: 'center' }}>Gun Details</Typography>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth variant="outlined" key={gun.scope}>
                  <InputLabel id="demo-multiple-checkbox-label">Scope</InputLabel>
                  <Select
                    value={gun.scope}
                    onChange={e => setGun({ ...gun, scope: e.target.value })}
                    label="Scope"
                  >
                    {scopes.map((scope) => (
                      <MenuItem key={scope.name} value={scope}>
                        {scope.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, scope: !locks.scope }))}>
                  {locks.scope ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth key={gun.stock}>
                  <InputLabel id="demo-multiple-checkbox-label">Stock</InputLabel>
                  <Select
                    value={gun.stock}
                    onChange={e => setGun({ ...gun, stock: e.target.value })}
                    label="Stock"
                  >
                    {stocks.map((stock) => (
                      <MenuItem key={stock.name} value={stock}>
                        {stock.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, stock: !locks.stock }))}>
                  {locks.stock ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth key={gun.action}>
                  <InputLabel id="demo-multiple-checkbox-label">Action</InputLabel>
                  <Select
                    value={gun.action}
                    onChange={e => setGun({ ...gun, action: e.target.value })}
                    label="Action"
                  >
                    {actions.map((action) => (
                      <MenuItem key={action.name} value={action}>
                        {action.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, action: !locks.action }))}>
                  {locks.action ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth key={gun.contour}>
                  <InputLabel id="demo-multiple-checkbox-label">Contour</InputLabel>
                  <Select
                    value={gun.contour}
                    onChange={e => setGun({ ...gun, contour: e.target.value })}
                    label="Contour"
                  >
                    {contours.map((contour) => (
                      <MenuItem key={contour.name} value={contour}>
                        {contour.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, contour: !locks.contour }))}>
                  {locks.contour ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <TextField
                  fullWidth
                  label="Barrel Length"
                  value={gun.barrelLength}
                  onChange={e => {
                    setGun({ ...gun, barrelLength: e.target.value })
                  }}
                  InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, barrelLength: !locks.barrelLength }))}>
                  {locks.barrelLength ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth key={gun.cartridge}>
                  <InputLabel id="demo-multiple-checkbox-label">Cartridge</InputLabel>
                  <Select
                    value={gun.cartridge}
                    onChange={e => setGun({ ...gun, cartridge: e.target.value })}
                    label="Cartridge"
                  >
                    {cartridges.map((cartridge) => (
                      <MenuItem key={cartridge.name} value={cartridge}>
                        {cartridge.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, cartridge: !locks.cartridge }))}>
                  {locks.cartridge ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <FormControl fullWidth key={gun.bullet}>
                  <InputLabel id="demo-multiple-checkbox-label">Bullet</InputLabel>
                  <Select
                    value={gun.bullet}
                    onChange={e => setGun({ ...gun, bullet: e.target.value })}
                    label="Bullet"
                  >
                    {bullets.map((bullet) => (
                      <MenuItem key={bullet.name} value={bullet}>
                        {bullet.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} style={{ paddingTop: 15 }}>
                <IconButton onClick={() => setLocks(locks => ({ ...locks, bullet: !locks.bullet }))}>
                  {locks.bullet ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} hidden={tab !== 2}>
          <Paper>
            <Grid container p={2} justifyContent="center">
              <Typography variant="h4">Results</Typography>
            </Grid>
            <Box p={1} pt={0}>
              <canvas ref={canvasRef} style={{ border: '1px solid black', width: '100%', aspectRatio: '1 / 1' }} />
            </Box>
            {results && <Box p={1} pt={0}>
              <Typography variant="h5" style={{ color: 'red' }}>
                First Round Hit Probability: {toFixed(results.prob1 * 100, 1)}%
              </Typography>
              <Typography variant="h5" style={{ color: '#3355ff' }}>
                Second Round Hit Probability: {toFixed(results.prob2 * 100, 1)}%
              </Typography>
              <Typography variant="h5">
                Averge 5 Shot Group: {toFixed(gun.groupSize, 2)} MOA
              </Typography>
              <Typography variant="h5">
                Rifle Weight: {toFixed(gun.weight, 1)} lb
              </Typography>
              {gun.v0 && <Typography variant="h5">
                Muzzle Velocity: {toFixed(gun.v0, 0)} fps
              </Typography>}
            </Box>}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}

function Variable ({ name, measured, actual, sd, required, units, using, size, shape, set, useSd, actualLabel, sdLabel }) {
  let columns = 3
  if (typeof actual !== 'string' && typeof measured !== 'string') columns = 9
  else if (typeof sd !== 'string' && typeof measured !== 'string') columns = 9
  else if (typeof sd !== 'string' || typeof measured !== 'string') columns = 4
  return <Grid container columnSpacing={2}>
    <Grid xs={12} sm={3} container alignItems="center" pl={2}>
      <Typography variant="h6">
        {toChipLabel(name)}
      </Typography>
    </Grid>
    {typeof size === 'string' && <Grid item xs={12} sm={4}>
      <TextField fullWidth label="Size" value={size} onChange={e => set('size', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }} />
    </Grid>}
    {typeof shape === 'string' && <Grid item xs={12} sm={4}>
      <FormControl fullWidth>
        <InputLabel>Shape</InputLabel>
        <Select
          fullWidth
          value={shape}
          label="Shape"
          onChange={e => set('shape', e.target.value)}
        >
          <MenuItem value="circle">Circle</MenuItem>
          <MenuItem value="square">Square</MenuItem>
          <MenuItem value="ipsc">IPSC</MenuItem>
        </Select>
      </FormControl>
    </Grid>}
    {typeof measured === 'string' && <Grid item xs={12} sm={columns}>
      <TextField fullWidth label="Measured" value={measured} onChange={e => set('measured', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }} />
    </Grid>}
    {typeof actual === 'string' && <Grid item xs={12} sm={columns}>
      <TextField
        fullWidth
        label={actualLabel ? actualLabel : 'Actual'}
        disabled={useSd === true}
        value={actual}
        onChange={e => set('actual', e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }}
      />
    </Grid>}
    {typeof useSd === 'boolean' && <Grid container item xs={1} justifyContent="center" alignItems="center">
        <Switch color="default" checked={useSd} onChange={e => set('useSd', e.target.checked)} />
    </Grid>}
    {typeof sd === 'string' && <Grid item xs={12} sm={columns}>
      <TextField
        fullWidth
        label={sdLabel ? sdLabel : 'Standard Deviation'}
        value={sd}
        disabled={useSd === false}
        onChange={e => set('sd', e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }}
      />
    </Grid>}
    {typeof using === 'boolean' && <Grid item xs={12} sm={columns}>
      <FormControlLabel
        control={<Switch checked={using} onChange={e => set('using', e.target.checked)} />}
        label={`Account for ${toChipLabel(name)}`}
      />
    </Grid>}
  </Grid>
}

function toChipLabel (key, value) {
	if (key === 'bc') {
    key = 'BC'
  } else if (key === 'v0') {
    key = 'Muzzle Velocity'
  } else {
    key = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
  }
  key = key.replace('Use ', '')
  return key
}

function getDefaultVariableSetting (name, profile, extras) {
  extras = extras || {}
  switch (name) {
    // env
    case  'target':              return  {  name, ...extras, size:      '20',                        shape:     'circle',                   units: 'in',                                                      }
    case  'iterations':          return  {  name, ...extras,                                        actual:    '1000',                     actualLabel: 'Iterations',                                         }
    case  'range':               return  {  name, ...extras, measured:  profile.range,              actual:    profile.range,              units: 'yards', sd:  '3',                                          }

    // driven
    case  'groupSize':           return  {  name, ...extras, sdLabel: 'DTC Standard Deviation', actualLabel: 'Avg Five Shot Group',     actual:  '1',                          units: 'moa',   sd: '.5',       useSd: false,                     }
    case  'v0':                  return  {  name, ...extras, /*measured:  profile.v0,                 actual:    profile.v0,*/                 units: 'fps',   sd:  '10',                                     }
    case  'bc':                  return  {  name, ...extras, measured:  profile.bc,                 actual:    profile.bc,                                 sd:  (profile.bc * .01).toFixed(4), advanced: true }

    case  'windSpeed':           return  {  name, ...extras, measured:  profile.wind[0].speed,      actual:    profile.wind[0].speed,      units: 'mph',   sd:  '2',                                          }
    case  'windDirection':       return  {  name, ...extras, measured:  profile.wind[0].direction,  actual:    profile.wind[0].direction,  units: 'deg',   sd:  '5',                                          }
    case  'useAerodynamicJump':  return  {  name, ...extras,                                        using:     profile.useAerodynamicJump,                                                     advanced: true }
    case  'useSpinDrift':        return  {  name, ...extras,                                        using:     profile.useSpinDrift,                                                           advanced: true }
    case  'latitude':            return  {  name, ...extras, measured:  profile.latitude,           actual:    profile.latitude,           units: 'deg',                                       advanced: true }
    case  'azimuth':             return  {  name, ...extras, measured:  profile.azimuth,            actual:    profile.azimuth,            units: 'deg',   sd:  '2' ,                          advanced: true }
    case  'lookAngle':           return  {  name, ...extras, measured:  profile.lookAngle,          actual:    profile.lookAngle,          units: 'deg',   sd:  '2',                           advanced: true }
    case  'cant':                return  {  name, ...extras, measured:  '0',                        actual:    '0',                        units: 'deg',   sd:  '0',                           advanced: true }
    case  'pressure':            return  {  name, ...extras, measured:  profile.pressure,           actual:    profile.pressure,           units: 'inHg',  sd:  '.1',                          advanced: true }
    case  'temperature':         return  {  name, ...extras, measured:  profile.temperature,        actual:    profile.temperature,        units: '°F',    sd:  '1',                           advanced: true }
    case  'verticalZero':        return  {  name, ...extras,                                        actual:    '0',                        units: 'moa',   actualLabel: 'Vertical Zero',       advanced: true }
    case  'horizontalZero':      return  {  name, ...extras,                                        actual:    '0',                        units: 'moa',   actualLabel: 'Horizontal Zero',     advanced: true }
    case  'humidity':            return  {  name, ...extras, measured:  profile.humidity,           actual:    profile.humidity,           units: '%',     sd:  '10',                          advanced: true }
    case  'scopeHeight':         return  {  name, ...extras, measured:  profile.scopeHeight,        actual:    profile.scopeHeight,        units: 'in',                                        advanced: true }
    default: throw new Error('unknown variable name')
  }
}

export default function WrappedGunDesigner () {
  const onload = data => {
    data.gun.action = actions.find(a => a.name === data.gun.action.name)
    data.gun.bullet = bullets.find(a => a.name === data.gun.bullet.name)
    data.gun.cartridge = cartridges.find(a => a.name === data.gun.cartridge.name)
    data.gun.contour = contours.find(a => a.name === data.gun.contour.name)
    data.gun.scope = scopes.find(a => a.name === data.gun.scope.name)
    data.gun.stock = stocks.find(a => a.name === data.gun.stock.name)
    return data
  }
  return <AutoSaveProvider onload={onload}>
    <GunDesigner />
  </AutoSaveProvider>
}
